import React, { useState, useEffect } from 'react';
import Card from '../shared/Card/Card';
import { ButtonInput } from '../shared/Inputs/ButtonInput';
import CustomJoditEditor from '../shared/Inputs/CustomJoditEditor';
import { ITemplatePolicy, ICreateUpdateTemplatePolicyRequest, TemplatePolicyType } from '../../models/TemplatePolicy';
import useTemplatePolicies from '../../data/useTemplatePolicies';
import useDefaultCRUDHandlers from '../../hooks/useDefaultCRUDHandlers';
import useNotification from "../notifications/useNotifications";

interface TemplatePolicyEditorProps {
  type: TemplatePolicyType;
  policy?: ITemplatePolicy;
}

const TemplatePolicyEditor: React.FC<TemplatePolicyEditorProps> = ({ type, policy }) => {
  const { create, update } = useTemplatePolicies();
  const { saveHandlers } = useDefaultCRUDHandlers('Template Policy');
  const { addNotification } = useNotification();
  const [content, setContent] = useState('');

  useEffect(() => {
    if (policy) {
      setContent(policy.description);
    } else {
      setContent('');
    }
  }, [policy]);

  const handleSave = async () => {

    try {
      if (policy) {
        // Update existing policy
        await update.mutateAsync({
          id: policy.id,
          request: {
            type: type,
            description: content
          }
        });
        saveHandlers.onSuccess();
      } else {
        // Create new policy
        await create.mutateAsync({
          type: type,
          description: content
        });
        saveHandlers.onSuccess();
      }
    } catch (err: any) {
      saveHandlers.onError();
    }
  };

  const getTypeDisplayName = (type: TemplatePolicyType) => {
    switch (type) {
      case TemplatePolicyType.Warranty:
        return 'Warranty Policy';
      case TemplatePolicyType.TermsAndConditions:
        return 'Terms & Conditions';
      case TemplatePolicyType.Returns:
        return 'Returns Policy';
      default:
        return type;
    }
  };

  const isLoading = create.isLoading || update.isLoading;

  return (
    <Card
      title={getTypeDisplayName(type)}
      containerClassName="mb-6"
      bodyClassName="p-6"
    >
      <div className="space-y-4">
        <CustomJoditEditor
          content={content}
          setContent={setContent}
          height={400}
          placeholder={`Enter ${getTypeDisplayName(type).toLowerCase()} content...`}
        />

        <div className="flex justify-between items-center">
          <div>
            {policy && (
              <p className="text-sm text-gray-500">
                Last updated: {policy.updated ? new Date(policy.updated).toLocaleString() : 'Never'}
              </p>
            )}
          </div>
          <ButtonInput
            label={isLoading ? 'Saving...' : policy ? 'Update Policy' : 'Create Policy'}
            onClick={handleSave}
            disabled={isLoading}
            isLoading={isLoading}
            classes=""
          />
        </div>
      </div>
    </Card>
  );
};

export default TemplatePolicyEditor; 